import { FC, useRef } from 'react';
import { useTranslation } from '@pypestream/translations';
import { proStudioUrqlGql } from '@pypestream/api-services/urql';

import {
  FileListSection,
  useConfirmDeleteModal,
  type FileListItem,
} from '../../components';
import { useAgentManagerFiles, useProStudioViewerRole } from '../../hooks';
import {
  AgentManagerConflictError,
  buildUploadChanges,
  downloadAgentManagerFilesAsZip,
  groupCustomAssets,
  readAssetConfigName,
  syncAgentManagerFiles,
  validateAssetFolder,
  type PickedAssetFile,
} from '../../utils';
import { sendUserMessage } from '../../xstate/app.xstate-utils';

const reportError = (error: unknown, fallback: string) =>
  sendUserMessage({
    type: 'error',
    text: error instanceof Error ? error.message : fallback,
    autoClose: 5000,
  });

/**
 * CX custom assets — Lit components authored with `@pypestream/pype-sdk` and packed by
 * `pypectl asset pack`.
 *
 * A row is an asset (a folder), not a file, so upload takes a directory and delete has
 * to remove every file underneath: `deleteAgentManagerFile` takes a single path and
 * there is no subtree delete.
 */
export const AssetsTab: FC = () => {
  const [t] = useTranslation();
  const isViewer = useProStudioViewerRole();
  const { files, isLoading, error, refetch, solutionId, versionName } =
    useAgentManagerFiles('custom-assets');
  const { confirm: confirmDelete, confirmDeleteModal } =
    useConfirmDeleteModal();

  const inputRef = useRef<HTMLInputElement>(null);

  const assets = groupCustomAssets(files);
  const items: FileListItem[] = assets.map((asset) => ({
    name: asset.name,
    path: asset.path,
    size: asset.size,
    mimeType: null,
  }));

  const handlePicked = async (fileList: FileList | null) => {
    if (!fileList?.length) return;
    if (!solutionId || !versionName) return;

    // `webkitRelativePath` is `<pickedFolder>/config.json`. Strip that first segment to
    // get the path within the asset, which is what validation and config reading expect.
    const withinAsset: PickedAssetFile[] = Array.from(fileList).map((file) => {
      const segments = (file.webkitRelativePath || file.name).split('/');
      return {
        relativePath: segments.slice(1).join('/') || file.name,
        file,
      };
    });

    const validation = validateAssetFolder(withinAsset);
    if (!validation.ok) {
      sendUserMessage({
        type: 'error',
        text: validation.reason,
        autoClose: 8000,
      });
      return;
    }

    // The asset's `embed_id` from config.json is authoritative — the picked folder
    // could be named anything (`dist/` after `pypectl asset pack`) — and it becomes
    // the directory under custom-assets/.
    const folderName =
      (fileList[0].webkitRelativePath || '').split('/')[0] || 'custom-asset';
    const assetName = (await readAssetConfigName(withinAsset)) || folderName;

    try {
      await syncAgentManagerFiles({
        solutionId,
        versionName,
        commitMessage: `Uploaded custom asset ${assetName}`,
        changes: buildUploadChanges({
          type: 'custom-assets',
          // Each path is prefixed with `custom-assets/` for us, so the asset folder and
          // the file's own nested path are all that belong here.
          files: withinAsset.map(({ relativePath, file }) => ({
            relativePath: `${assetName}/${relativePath}`,
            file,
          })),
          // The flat file list, not `assets`: the sha lives per file, while
          // `groupCustomAssets` collapses each asset folder into one row.
          existing: files,
        }),
      });
      refetch();
    } catch (uploadError) {
      if (uploadError instanceof AgentManagerConflictError) refetch();
      reportError(uploadError, 'Error uploading custom asset');
    }
  };

  const handleDelete = async (item: FileListItem) => {
    if (!solutionId || !versionName) return;
    const asset = assets.find((candidate) => candidate.path === item.path);
    if (!asset) return;
    if (!(await confirmDelete(item.name))) return;

    const paths = asset.files.map((file) => file.path || '').filter(Boolean);

    // One call per file. Report exactly which ones survived rather than claiming
    // success — a partial failure leaves a half-deleted asset folder behind.
    const results = await Promise.allSettled(
      paths.map((filePath) =>
        proStudioUrqlGql
          .deleteAgentManagerFile({ filePath, solutionId, versionName })
          .then((response) => {
            if (response.error) throw new Error(response.error.message);
          })
      )
    );

    refetch();

    const failed = paths.filter(
      (_, index) => results[index].status === 'rejected'
    );
    if (failed.length) {
      sendUserMessage({
        type: 'error',
        text: `${item.name} was only partly deleted — these files remain: ${failed.join(', ')}`,
        autoClose: 8000,
      });
    }
  };

  const handleDownload = async (item: FileListItem) => {
    if (!solutionId || !versionName) return;
    const asset = assets.find((candidate) => candidate.path === item.path);
    if (!asset) return;
    try {
      await downloadAgentManagerFilesAsZip({
        solutionId,
        versionName,
        paths: asset.files.map((file) => file.path || '').filter(Boolean),
        zipName: item.name,
      });
    } catch (downloadError) {
      reportError(downloadError, `Error downloading ${item.name}`);
    }
  };

  const handleDownloadAll = async () => {
    if (!solutionId || !versionName) return;
    try {
      await downloadAgentManagerFilesAsZip({
        solutionId,
        versionName,
        paths: files.map((file) => file.path || '').filter(Boolean),
        zipName: `${versionName}-custom-assets`,
      });
    } catch (downloadError) {
      reportError(downloadError, 'Error downloading custom assets');
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        hidden
        multiple
        // Directory picking is non-standard but supported in Chromium and WebKit;
        // Filepond cannot select a folder, which is why this is a bare input.
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        webkitdirectory="true"
        onChange={(event) => {
          void handlePicked(event.target.files);
          event.target.value = '';
        }}
      />
      <FileListSection
        testIdPrefix="ps-assets"
        title={
          t('pro-studio/actions:assets.title', { defaultValue: 'Assets' }) ?? ''
        }
        tooltip={
          t('pro-studio/actions:assets.tooltip', {
            defaultValue:
              'Custom assets are built with the Pype SDK and packed with `pypectl asset pack`.',
          }) ?? ''
        }
        items={items}
        readOnly={isViewer}
        uploadDisabled={!versionName}
        uploadLabel={
          t('pro-studio/actions:assets.action', {
            defaultValue: 'Upload Assets',
          }) ?? ''
        }
        rowActions={['download', 'delete']}
        isLoading={isLoading}
        error={error}
        emptyLabel={
          t('pro-studio/actions:assets.empty', {
            defaultValue: 'No custom assets yet.',
          }) ?? ''
        }
        onUploadClick={() => inputRef.current?.click()}
        onDownloadAll={() => void handleDownloadAll()}
        onDownload={(item) => void handleDownload(item)}
        onDelete={(item) => void handleDelete(item)}
      />
      {confirmDeleteModal}
    </>
  );
};
